import { Box, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitCard, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { MobileActionItem } from '../common/MobileCrud';
import {
    KitAccordion,
    KitChip,
    KitChips,
    KitEmpty,
    KitFormBar,
    KitInput,
    KitProgress,
    KitThumb,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { INVENTORY_STATUS } from '../../pages/inventory/inventoryStatus';

/**
 * Detalhe/contagem do inventário no shell mobile — `prototipos-mobile/inventario/detalhe.html`.
 * Só apresentação: dados, contagem, scan e ações de status vêm de `pages/inventory/View.js`.
 */

const PackageIcon = protoIcon('package');
const ScanIcon = protoIcon('scan');
const DothIcon = protoIcon('doth');
const CameraIcon = protoIcon('camera');
const HistoryIcon = protoIcon('history');
const DownloadIcon = protoIcon('download');
const XIcon = protoIcon('x');
const ChartIcon = protoIcon('clipcheck');

const PAGE_SIZE = 30;
const FILTERS = [
    { key: 'all', label: 'Todos' },
    { key: 'pending', label: 'Pendentes' },
    { key: 'counted', label: 'Contados' },
    { key: 'divergent', label: 'Divergentes' },
];

const isCounted = (item) => item.counted_qty !== null && item.counted_qty !== undefined;
const isDivergent = (item) => item.difference !== null && item.difference !== undefined && item.difference !== 0;

function formatDateTime(value) {
    if (!value) return '—';
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function SummaryCell({ label, value, color }) {
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: '8px 10px', minWidth: 0, backgroundColor: 'background.paper' }}>
            <Box sx={{ fontSize: 10.5, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.04em', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
            <Box sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.3, color: color || 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</Box>
        </Box>
    );
}

export default function MobileInventoryDetail({
    loading,
    inventory,
    summary,
    items,
    canEdit,
    canApprove,
    canExport,
    canCountItems,
    edits,
    setEdits,
    inputRefs,
    commitCount,
    diffColor,
    openHistory,
    onSaveAll,
    onAction,
    onExport,
    scan,
}) {
    const [filter, setFilter] = useState('all');
    const [limit, setLimit] = useState(PAGE_SIZE);
    const [expanded, setExpanded] = useState(null);
    const [summaryOpen, setSummaryOpen] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);

    const status = inventory?.status;
    const isCounting = status === 'counting';
    const canScan = Boolean(inventory) && isCounting && canEdit;
    const canCancel = canEdit && ['draft', 'counting', 'review'].includes(status);
    const hasMore = canExport || canCancel;

    useAppBarAction({
        enabled: Boolean(inventory),
        actions: [
            ...(canScan ? [{ label: 'Ler código', icon: ScanIcon, onClick: () => setScanOpen(true) }] : []),
            ...(hasMore ? [{ label: 'Mais', icon: DothIcon, onClick: () => setMoreOpen(true) }] : []),
        ],
    });

    if (loading && !inventory) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
                <Skeleton variant="rounded" height={180} sx={{ borderRadius: '16px' }} />
            </Box>
        );
    }
    if (!inventory) return null;

    const statusInfo = INVENTORY_STATUS[status] || INVENTORY_STATUS.draft;
    const countedCount = items.filter(isCounted).length;
    const pct = items.length > 0 ? Math.round((countedCount / items.length) * 100) : 0;

    const counts = {
        all: items.length,
        pending: items.length - countedCount,
        counted: countedCount,
        divergent: items.filter(isDivergent).length,
    };
    const filtered = items
        .filter((i) => (filter === 'pending' ? !isCounted(i) : filter === 'counted' ? isCounted(i) : filter === 'divergent' ? isDivergent(i) : true))
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'));
    const visible = filtered.slice(0, limit);

    const showFormBar = (canEdit && ['draft', 'counting', 'review'].includes(status)) || (canApprove && status === 'review');

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <Box component="b" sx={{ minWidth: 0, overflowWrap: 'anywhere', color: inventory.description ? 'text.primary' : 'text.disabled' }}>{inventory.description || 'Sem descrição'}</Box>
                    <KitPill tone={statusInfo.tone}>{statusInfo.label}</KitPill>
                </Box>
                <KitProgress value={pct} sx={{ mt: '12px' }} />
                <Box sx={{ fontSize: 12.5, color: 'text.disabled', mt: '8px' }}>{countedCount} de {items.length} itens contados</Box>
                <Box sx={{ fontSize: 12, color: 'text.disabled', mt: '8px' }}>
                    {inventory.scope === 'partial' ? 'Parcial' : 'Completo'}{inventory.location_label ? ` · Local: ${inventory.location_label}` : ''}
                    <br />
                    Criado por {inventory.created_by || '—'} em {formatDateTime(inventory.created_at)}
                    {inventory.started_at ? ` · Iniciado por ${inventory.started_by || '—'} em ${formatDateTime(inventory.started_at)}` : ''}
                    {inventory.finished_at ? ` · Finalizado por ${inventory.finished_by || '—'} em ${formatDateTime(inventory.finished_at)}` : ''}
                </Box>
            </KitCard>

            {summary && (
                <Box sx={{ mt: '12px' }}>
                    <KitAccordion
                        open={summaryOpen}
                        icon={ChartIcon}
                        title="Resumo da contagem"
                        subtitle={`${summary.counted} de ${summary.total_products} produtos contados`}
                        onToggle={() => setSummaryOpen((o) => !o)}
                    >
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', pb: '8px' }}>
                            <SummaryCell label="Total de produtos" value={summary.total_products} />
                            <SummaryCell label="Contados" value={summary.counted} />
                            <SummaryCell label="Sem divergência" value={summary.no_divergence} />
                            <SummaryCell label="Qtd. ajustada" value={summary.adjusted_qty} />
                            <SummaryCell label="Com falta" value={`${summary.shortage} (${summary.shortage_qty} un.)`} color="error.main" />
                            <SummaryCell label="Com sobra" value={`${summary.surplus} (${summary.surplus_qty} un.)`} color="success.main" />
                            <SummaryCell label="Perdas (R$)" value={summary.loss_value} color="error.main" />
                            <SummaryCell label="Sobras (R$)" value={summary.gain_value} color="success.main" />
                        </Box>
                    </KitAccordion>
                </Box>
            )}

            <SecH title="Itens" />

            <KitChips sx={{ mb: '10px' }}>
                {FILTERS.map((f) => (
                    <KitChip key={f.key} active={filter === f.key} count={counts[f.key]} onClick={() => { setFilter(f.key); setLimit(PAGE_SIZE); }}>
                        {f.label}
                    </KitChip>
                ))}
            </KitChips>

            {filtered.length === 0 ? (
                <KitEmpty icon={PackageIcon} title="Nenhum produto encontrado" />
            ) : (
                <KitList>
                    {visible.map((item) => {
                        const open = expanded === item.id;
                        const hasDiff = item.difference !== null && item.difference !== undefined;
                        const diffText = hasDiff ? (item.difference > 0 ? `+${item.difference}` : item.difference) : '';
                        return (
                            <Box
                                key={item.id}
                                onClick={() => setExpanded(open ? null : item.id)}
                                sx={{ p: '13px 14px', minHeight: 60, boxSizing: 'border-box', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <KitThumb icon={PackageIcon} iconSize={20} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</Box>
                                        <Box sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: '1px' }}>
                                            Sistema: {item.expected_qty}
                                            {hasDiff && (
                                                <>
                                                    {' · Dif. '}
                                                    <Box component="span" sx={{ fontWeight: 700, color: diffColor(item.difference) }}>{diffText}</Box>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                    {canCountItems ? (
                                        <KitInput
                                            type="number"
                                            min={0}
                                            inputMode="numeric"
                                            aria-label={`Quantidade contada de ${item.name}`}
                                            ref={(el) => { inputRefs.current[item.id] = el; }}
                                            value={edits[item.id] !== undefined ? edits[item.id] : (item.counted_qty ?? '')}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => setEdits((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                            onBlur={() => commitCount(item)}
                                            sx={{ width: 72, height: 42, textAlign: 'center', p: '0 4px', flexShrink: 0 }}
                                        />
                                    ) : (
                                        <Box sx={{ fontSize: 16, fontWeight: 800, flexShrink: 0 }}>{item.counted_qty ?? '—'}</Box>
                                    )}
                                </Box>
                                {open && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', mt: '8px', pl: '56px', flexWrap: 'wrap', fontSize: 12, color: 'text.secondary' }}>
                                        {item.code && <span>Cód.: {item.code}</span>}
                                        <span>Custo: R$ {item.cost}</span>
                                        <span>Impacto: {hasDiff ? `R$ ${item.impact}` : '—'}</span>
                                        <Box
                                            component="button"
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openHistory(item); }}
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minHeight: 44, m: '-12px 0', p: '0 4px', border: 0, background: 'none', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: 'primary.main', cursor: 'pointer' }}
                                        >
                                            <HistoryIcon size={14} /> Histórico
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </KitList>
            )}

            {filtered.length > limit && (
                <KitButton block sx={{ mt: '14px' }} onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                    Mostrar mais ({filtered.length - limit} restantes)
                </KitButton>
            )}

            {showFormBar && (
                <KitFormBar>
                    {status === 'draft' && (
                        <KitButton variant="primary" onClick={() => onAction('start')}>Iniciar contagem</KitButton>
                    )}
                    {(status === 'counting' || status === 'review') && canEdit && (
                        <KitButton onClick={onSaveAll}>Salvar parcial</KitButton>
                    )}
                    {status === 'counting' && canEdit && (
                        <KitButton variant="primary" onClick={() => onAction('submit')}>Finalizar</KitButton>
                    )}
                    {status === 'review' && canApprove && (
                        <KitButton variant="primary" onClick={() => onAction('approve')}>Aprovar e ajustar</KitButton>
                    )}
                </KitFormBar>
            )}

            <MobileSheet open={scanOpen} onClose={() => setScanOpen(false)} title="Ler código">
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <KitInput
                        ref={scan.inputRef}
                        autoFocus
                        value={scan.code}
                        onChange={(e) => scan.setCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); scan.onScan(); } }}
                        placeholder="Código do produto"
                        aria-label="Código do produto"
                        sx={{ flex: 1, minWidth: 0 }}
                    />
                    <KitButton aria-label="Ler com a câmera" onClick={() => { setScanOpen(false); scan.onCamera(); }} sx={{ width: 50, minWidth: 50, p: 0 }}>
                        <CameraIcon size={20} />
                    </KitButton>
                </Box>
                {scan.last && (
                    <Box sx={{ mt: '14px' }}>
                        <Box sx={{ fontSize: 12, color: 'text.disabled' }}>Código: {scan.last.code}</Box>
                        <Box sx={{ fontSize: 15, fontWeight: 700 }}>{scan.last.name}</Box>
                        <Box sx={{ fontSize: 20, fontWeight: 800, color: 'primary.main' }}>Contagem atual: {scan.last.counted_qty}</Box>
                    </Box>
                )}
            </MobileSheet>

            <MobileSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Mais">
                {canExport && (
                    <>
                        <MobileActionItem icon={DownloadIcon} label="Excel (CSV)" onClick={() => { setMoreOpen(false); onExport('csv'); }} />
                        <MobileActionItem icon={DownloadIcon} label="PDF" onClick={() => { setMoreOpen(false); onExport('pdf'); }} />
                    </>
                )}
                {canCancel && (
                    <MobileActionItem icon={XIcon} label="Cancelar inventário" destructive chevron={false} onClick={() => { setMoreOpen(false); onAction('cancel'); }} />
                )}
            </MobileSheet>
        </Box>
    );
}
